import React, { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

const useStyles = makeStyles((theme) => ({
    barContainer: {
        display: 'flex',
        height: theme.spacing(4.5),
        width: '100%',
        borderRadius: '0.5rem',
        overflow: 'hidden',
    },
    section: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.875rem',
        cursor: 'pointer',
        transition: 'filter 0.2s',
        '&:hover': {
            filter: 'brightness(1.1)',
        },
        height: '2.25rem'
    },
    separator: {
        width: theme.spacing(0.25),
        backgroundColor: '#fff',
    },
    tooltip: {
        boxShadow: 'none',
        borderColor: 'hsl(219 100 93)',
        borderRadius: '0.5rem',
        border: '1px solid',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    leftGroup: {
        display: 'flex',
        alignItems: 'center',
    },
    colorDot: {
        width: theme.spacing(1.5),
        height: theme.spacing(1.5),
        borderRadius: theme.spacing(0.6),
        marginRight: theme.spacing(1),
    },
    legendContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    labelContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2)
    },
    labelText: {
        fontSize: theme.typography.caption.fontSize,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: theme.spacing(16),

        [theme.breakpoints.up('xs')]: { width: theme.spacing(10) },
        [theme.breakpoints.up('sm')]: { width: theme.spacing(10) },
        [theme.breakpoints.up('md')]: { width: theme.spacing(8) },
        [theme.breakpoints.up('lg')]: { width: theme.spacing(20) },
        [theme.breakpoints.up('xl')]: { width: theme.spacing(38) },
    },
    percentageText: {
        fontSize: theme.typography.caption.fontSize,
    },
    legendGridContainer: {
        marginTop: theme.spacing(1.5)
    },
}));

export const colours = {
    positive: 'hsl(162, 70%, 40%, 0.9)',
    neutral: 'rgba(156, 163, 175, 0.55)',
    risky: 'rgba(251, 146, 60, 0.8)'
}

export const ranges = [
    { min: 0, max: 29, colour: colours.risky },
    { min: 30, max: 69, colour: colours.neutral },
    { min: 70, max: 100, colour: colours.positive }
]

export function getBarColour(score, qualified, isPositiveMetric) {
    if (isPositiveMetric === undefined) {
        return ranges.find(range => score >= range.min && score <= range.max)?.colour ?? 'bg-transparent'
    }

    if (!isPositiveMetric && qualified) {
        return colours.risky
    }

    if (isPositiveMetric && qualified) {
        return colours.positive
    }

    return colours.neutral
}
const getScoreRangeIndex = (score) => {
    return ranges.findIndex(range => score >= range.min && score <= range.max);
}

export function splitTooltipContentText(text, maxLength = 40) {
    const words = text.trim().split(/\s+/)
    const lines = []
    let currentLine = ''

    words.forEach(word => {
        const nextLine = currentLine ? `${currentLine} ${word}` : word

        if (nextLine.length <= maxLength) {
            currentLine = nextLine
            return
        }

        if (currentLine) {
            lines.push(currentLine)
        }
        currentLine = word
    })

    if (currentLine) lines.push(currentLine)

    return lines
}

const LegendItem = ({ section, isPositiveMetric }) => {
    const classes = useStyles()

    const colour = getBarColour(section.score, section.qualified, isPositiveMetric)
    const percentageToDisplay = Math.round(section.percentage)
    const ratioSourceToDisplay = section.response_count

    return (
        <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={4}
        >
            <div
                className={classes.legendContainer}
            >
                <div className={classes.labelContainer}>
                    <div className={classes.colorDot} style={{ backgroundColor: colour }} />
                    <div className={classes.labelText}>
                        {section.text}
                    </div>
                </div>
                <div className={classes.percentageText}>{percentageToDisplay}% ({ratioSourceToDisplay})</div>
            </div>
        </Grid>
    )
}

const BarSection = ({ section, isPositiveMetric, total, isNotLastSection }) => {
    const classes = useStyles()

    const backgroundColor = getBarColour(section.score, section.qualified, isPositiveMetric)
    const percentage = (section.percentage / total) * 100
    const ratioSourceToDisplay = section.response_count
    const percentageToDisplay = Math.round(section.percentage)

    return (
        <React.Fragment>
            <Tooltip
                classes={{
                    tooltip: classes.tooltip,
                }}
                placement="top"
                title={
                    <div
                        key={`section-${section.question_choice_id}-tooltip-content`}
                        data-testid={`section-${section.question_choice_id}-tooltip-content`}>
                        {splitTooltipContentText(section.text, 40).map((line) => (
                            <Typography key={`line-${line}`} variant="body2">
                                {line}
                            </Typography>
                        ))}
                        <div className={classes.row}>
                            <div className={classes.leftGroup}>
                                <Box className={classes.colorDot} style={{ backgroundColor }} />
                                <Typography variant="body2">{percentageToDisplay}%</Typography>
                            </div>
                            <Typography variant="body2">({ratioSourceToDisplay})</Typography>
                        </div>
                    </div>
                }
            >
                <Box
                    className={classes.section}
                    style={{ width: `${percentage}%`, backgroundColor }}
                >
                    {section.percentage > 10 && `${percentageToDisplay}%`}
                </Box>
            </Tooltip>
            {isNotLastSection && (
                <Box className={classes.separator} />
            )}
        </React.Fragment>
    )
}

const DataDistributionBar = ({ sections, isPositiveMetric, showLegend }) => {
    const classes = useStyles()

    const total = useMemo(() => {
        return sections.reduce((prev, curr) => prev + curr.percentage, 0)
    }, [sections])

    const sortedSections = useMemo(() => {
        return [...sections]
            .sort((a, b) => {
                if (isPositiveMetric === undefined && getScoreRangeIndex(a.score) === getScoreRangeIndex(b.score)) {
                    return b.percentage - a.percentage
                }
                if (isPositiveMetric === undefined) {
                    return b.score - a.score
                }
                if (a.qualified !== b.qualified) {
                    return a.qualified ? -1 : 1
                }
                return b.percentage - a.percentage;
            })
    }, [sections])

    return (
        <>
            <Box className={classes.barContainer}>
                {sortedSections.map((section, idx) => {
                    const isNotLastSection = idx < sections.length - 1

                    return (
                        <BarSection key={`bar-section-${section.question_choice_id}`} section={section} isNotLastSection={isNotLastSection} isPositiveMetric={isPositiveMetric} total={total} />
                    )})
                }
            </Box>
            {showLegend &&
                <Grid container spacing={1} className={classes.legendGridContainer}>
                    {sortedSections.map(section =>
                        <LegendItem key={`section-${section.question_choice_id}-legend-${section.text}`} section={section} isPositiveMetric={isPositiveMetric} />
                    )}
                </Grid>
            }
        </>
    )
}

export default DataDistributionBar